import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useData } from '../../hooks/useData';
import { useDocuments } from '../../hooks/useDocuments';
import { useVendorQuotes } from '../../hooks/useVendorQuotes';
import { useVendorCreate } from '../../hooks/useVendorCreate';
import {
  X,
  Trash,
  Info,
  CaretLeft,
  CaretRight,
  MagnifyingGlass,
  Paperclip,
  File,
  Plus,
} from '@phosphor-icons/react';
import { TABLES, CONSTRUCTION_TYPE_IDS } from '../../constants';
import { QUOTE_STATUS_OPTIONS as QUOTE_STATUSES } from '../../constants/quoteStatus';
import { getEffectiveEstimatedCost } from '../../utils/costCalculations';
import modalStyles from '../../styles/modals.module.css';
import styles from './QuoteFormModal.module.css';

/**
 * QuoteFormModal - Multi-step wizard for creating/editing vendor quotes
 *
 * Steps:
 * 1. Quote Details - vendor, status, dates, terms, attachments
 * 2. Item Selection - filter by space, search, select project items
 * 3. Review & Price - set prices per item, review before submit
 */

const STEPS = [
  { id: 'details', label: 'פרטי הצעה' },
  { id: 'items', label: 'בחירת פריטים' },
  { id: 'review', label: 'סקירה ומחירים' },
];

const KLALI_SPACE_ID = '__klali__';
const KLALI_SPACE_OPTION = { id: KLALI_SPACE_ID, name: 'כללי - פריטים לכלל הבית' };
const CONSTRUCTION_SPACE_ID = '__construction__';
const CONSTRUCTION_SPACE_OPTION = { id: CONSTRUCTION_SPACE_ID, name: 'בנייה - עבודות שלד ותשתיות' };

const QuoteFormModal = ({
  onClose,
  onSubmitRef,
  quote = null, // Existing quote for edit mode
  projectId,
  preselectedItems = [], // Pre-selected project item IDs
  defaultVendorTypeId = null, // When set, vendor picker is filtered to this type
  initialMode = 'full', // 'full' (default) | 'selected-only' — Step 2 item list mode
}) => {
  const { currentProject, showSuccess, showError } = useApp();
  const effectiveProjectId = projectId || currentProject?.id;

  // Wizard step state
  const [currentStep, setCurrentStep] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    master_vendor_id: quote?.master_vendor_id || '',
    total_price: quote?.total_price || '',
    validity_date: quote?.validity_date || '',
    lead_time_days: quote?.lead_time_days || '',
    payment_terms: quote?.payment_terms || '',
    notes: quote?.notes || '',
    status: quote?.status || 'draft',
    attachments: quote?.attachments || [],
  });

  // Line items state - selected items for the quote
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectImmediately, setSelectImmediately] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Item list mode — 'selected-only' shows only preselected items with an opt-in "add more" button
  const [itemListMode, setItemListMode] = useState(initialMode);

  // Item selection filters
  const [spaceFilter, setSpaceFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // New vendor form state
  const [showNewVendorForm, setShowNewVendorForm] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [newVendorTypeId, setNewVendorTypeId] = useState(defaultVendorTypeId || '');
  const [isCreatingVendor, setIsCreatingVendor] = useState(false);

  // Document upload
  const { uploadDocument, isUploading } = useDocuments({
    projectId: effectiveProjectId,
    recordId: quote?.id || 'new',
    recordType: 'quote',
  });

  // Vendor quotes hook for selecting quote immediately
  const { selectQuote } = useVendorQuotes(effectiveProjectId);

  // Centralized vendor creation hook
  const { createVendor, vendorTypesGrouped } = useVendorCreate();

  // Fetch data
  const { data: masterVendors, isLoading: vendorsLoading } = useData(TABLES.MASTER_VENDORS, {
    enabled: !!effectiveProjectId,
  });

  const { data: projectItems, isLoading: itemsLoading } = useData(TABLES.PROJECT_ITEMS, {
    select:
      '*, master_items(id, item_name, estimated_cost, item_options, cost_type_id, item_type_id)',
    filter: { project_id: effectiveProjectId },
    enabled: !!effectiveProjectId,
  });

  const { data: masterItems, isLoading: masterItemsLoading } = useData(TABLES.MASTER_ITEMS, {
    enabled: !!effectiveProjectId,
  });

  // Fetch floors first to get floor IDs for this project
  const { data: projectFloors, isLoading: floorsLoading } = useData(TABLES.PROJECT_FLOORS, {
    filter: { project_id: effectiveProjectId },
    enabled: !!effectiveProjectId,
  });

  // Get floor IDs for spaces query
  const floorIds = projectFloors?.map(f => f.id) || [];

  const { data: projectSpaces, isLoading: spacesLoading } = useData(TABLES.PROJECT_SPACES, {
    filter: { floor_id: floorIds },
    enabled: !!effectiveProjectId && floorIds.length > 0,
  });

  // Fetch existing quote line items for edit mode
  const { data: existingQuoteItems, isLoading: quoteItemsLoading } = useData(
    TABLES.VENDOR_QUOTE_ITEMS,
    {
      filter: { quote_id: quote?.id },
      enabled: !!quote?.id,
    },
  );

  // Mutations
  const { insert: insertQuote, update: updateQuote } = useData(TABLES.VENDOR_QUOTES);
  const { insert: insertQuoteItem, remove: removeQuoteItem } = useData(TABLES.VENDOR_QUOTE_ITEMS);

  // Filter master vendors by type when defaultVendorTypeId is set (trade-scoped quote creation)
  const filteredMasterVendors = useMemo(() => {
    if (!masterVendors) return [];
    if (!defaultVendorTypeId) return masterVendors;
    return masterVendors.filter(v => v.vendor_type_id === defaultVendorTypeId);
  }, [masterVendors, defaultVendorTypeId]);

  // Create lookup maps
  const masterItemsMap = useMemo(() => {
    if (!masterItems) return new Map();
    return new Map(masterItems.map(item => [item.id, item]));
  }, [masterItems]);

  // Create spaces lookup map
  const spacesMap = useMemo(() => {
    if (!projectSpaces) return new Map();
    return new Map(projectSpaces.map(s => [s.id, s]));
  }, [projectSpaces]);

  // Space options for filter dropdown
  const spaceOptions = useMemo(() => {
    const options = [{ id: '', name: 'כל החללים' }, KLALI_SPACE_OPTION, CONSTRUCTION_SPACE_OPTION];
    if (projectSpaces) {
      options.push(...projectSpaces.map(s => ({ id: s.id, name: s.name })));
    }
    return options;
  }, [projectSpaces]);

  // Track initialization state with a ref to avoid race conditions
  const initializationRef = useRef({
    hasRun: false,
    preselectedItemsSnapshot: null,
  });

  // Initialize selected items from preselected or existing quote items (runs once)
  useEffect(() => {
    // Skip if already initialized
    if (initializationRef.current.hasRun) return;

    // For edit mode, wait for existingQuoteItems to load
    if (quote?.id) {
      if (!existingQuoteItems) return;

      // Edit mode - load existing quote items
      const items = existingQuoteItems.map(qi => {
        const projectItemId = qi.project_item_ids?.[0];
        let displayName = 'פריט';
        let estimatedCost = null;

        if (projectItemId && projectItems) {
          const pi = projectItems.find(p => p.id === projectItemId);
          if (pi?.master_items) {
            displayName = pi.master_items.item_name;
            estimatedCost = getEffectiveEstimatedCost(pi, pi.master_items);
          }
        }

        return {
          id: qi.id,
          type: 'project_item',
          sourceId: projectItemId,
          displayName,
          estimatedCost,
          line_price: qi.line_price || '',
          line_notes: qi.line_notes || '',
          isExisting: true,
        };
      });
      setSelectedItems(items);
      initializationRef.current.hasRun = true;
      return;
    }

    // For new quotes with preselected project items
    if (preselectedItems.length > 0) {
      // Wait for projectItems to load with actual data
      if (!projectItems || projectItems.length === 0) return;

      // Store snapshot to detect if we've already processed these items
      const snapshotKey = preselectedItems.join(',');
      if (initializationRef.current.preselectedItemsSnapshot === snapshotKey) return;

      const items = preselectedItems
        .map(itemId => {
          const pi = projectItems.find(p => p.id === itemId);
          // Skip if item not found in project items
          if (!pi) return null;

          const masterItem = pi.master_items;
          const estimatedCost = masterItem ? getEffectiveEstimatedCost(pi, masterItem) : null;
          return {
            id: crypto.randomUUID(),
            type: 'project_item',
            sourceId: itemId,
            displayName: masterItem?.item_name || 'פריט',
            estimatedCost,
            line_price: '',
            line_notes: estimatedCost ? `מחיר משוער: ₪${estimatedCost.toLocaleString()}` : '',
            isExisting: false,
          };
        })
        .filter(Boolean); // Remove nulls from items not found

      setSelectedItems(items);
      initializationRef.current.hasRun = true;
      initializationRef.current.preselectedItemsSnapshot = snapshotKey;
      return;
    }

    // No preselected items - mark as initialized
    initializationRef.current.hasRun = true;
  }, [quote?.id, existingQuoteItems, preselectedItems, projectItems]);

  // Combined list of all selectable items (project items + BOQ items)
  const allSelectableItems = useMemo(() => {
    const items = [];

    // Add project items
    if (projectItems) {
      projectItems.forEach(pi => {
        const masterItem = pi.master_items || masterItemsMap.get(pi.master_item_id);
        const spaceId = pi.space_id;
        const space = spaceId ? spacesMap.get(spaceId) : null;
        const spaceName = space?.name || (spaceId ? 'חלל' : 'כללי');
        const estimatedCost = masterItem ? getEffectiveEstimatedCost(pi, masterItem) : null;

        // Determine virtual space: construction items (space_id null + construction type) vs כללי
        const itemTypeId = pi.master_items?.item_type_id || masterItem?.item_type_id;
        const virtualSpaceId =
          !spaceId && CONSTRUCTION_TYPE_IDS.includes(itemTypeId)
            ? CONSTRUCTION_SPACE_ID
            : KLALI_SPACE_ID;

        items.push({
          id: pi.id,
          type: 'project_item',
          displayName: masterItem?.item_name || 'פריט',
          spaceId: spaceId || virtualSpaceId,
          spaceName: spaceId
            ? spaceName
            : virtualSpaceId === CONSTRUCTION_SPACE_ID
              ? 'בנייה'
              : 'כללי',
          estimatedCost,
        });
      });
    }

    return items;
  }, [projectItems, masterItemsMap, spacesMap]);

  // Preselected project item IDs as a Set for fast lookup in 'selected-only' mode
  const preselectedItemIdSet = useMemo(() => new Set(preselectedItems), [preselectedItems]);

  // Filtered items based on space, search, and item list mode
  const filteredItems = useMemo(() => {
    return allSelectableItems.filter(item => {
      // Selected-only mode: only show items that were preselected (project items only)
      if (itemListMode === 'selected-only') {
        if (item.type !== 'project_item') return false;
        if (!preselectedItemIdSet.has(item.id)) return false;
      }

      // Space filter
      if (spaceFilter) {
        if (spaceFilter === KLALI_SPACE_ID) {
          if (item.spaceId !== KLALI_SPACE_ID) return false;
        } else if (spaceFilter === CONSTRUCTION_SPACE_ID) {
          if (item.spaceId !== CONSTRUCTION_SPACE_ID) return false;
        } else if (item.spaceId !== spaceFilter) {
          return false;
        }
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!item.displayName.toLowerCase().includes(query)) return false;
      }

      return true;
    });
  }, [allSelectableItems, spaceFilter, searchQuery, itemListMode, preselectedItemIdSet]);

  // Check if an item is already selected
  const isItemSelected = useCallback(
    (itemId, itemType) => {
      return selectedItems.some(si => si.sourceId === itemId && si.type === itemType);
    },
    [selectedItems],
  );

  // Toggle item selection
  const toggleItemSelection = useCallback(
    item => {
      const isSelected = isItemSelected(item.id, item.type);

      if (isSelected) {
        setSelectedItems(prev =>
          prev.filter(si => !(si.sourceId === item.id && si.type === item.type)),
        );
      } else {
        const newItem = {
          id: crypto.randomUUID(),
          type: item.type,
          sourceId: item.id,
          displayName: item.displayName,
          estimatedCost: item.estimatedCost,
          line_price: '',
          line_notes: item.estimatedCost
            ? `מחיר משוער: ₪${item.estimatedCost.toLocaleString()}`
            : '',
          isExisting: false,
        };
        setSelectedItems(prev => [...prev, newItem]);
      }
    },
    [isItemSelected],
  );

  // Remove selected item
  const removeSelectedItem = useCallback(itemId => {
    setSelectedItems(prev => prev.filter(si => si.id !== itemId));
  }, []);

  // Update selected item field
  const updateSelectedItem = useCallback((itemId, field, value) => {
    setSelectedItems(prev =>
      prev.map(item => (item.id === itemId ? { ...item, [field]: value } : item)),
    );
  }, []);

  // Handle form field changes
  const handleFieldChange = useCallback(
    (field, value) => {
      setFormData(prev => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: null }));
      }
    },
    [errors],
  );

  // Handle creating a new vendor
  const handleCreateVendor = useCallback(async () => {
    setIsCreatingVendor(true);
    try {
      const result = await createVendor({
        name: newVendorName,
        vendor_type_id: newVendorTypeId,
        source: 'user-generated',
      });

      if (!result.success) {
        showError(result.error);
        return;
      }

      // Select the newly created vendor
      setFormData(prev => ({ ...prev, master_vendor_id: result.vendor.id }));
      setShowNewVendorForm(false);
      setNewVendorName('');
      setNewVendorTypeId('');
      showSuccess('הספק נוצר בהצלחה');
    } catch (error) {
      console.error('Error creating vendor:', error);
      showError('שגיאה ביצירת הספק');
    } finally {
      setIsCreatingVendor(false);
    }
  }, [newVendorName, newVendorTypeId, createVendor, showSuccess, showError]);

  // Handle file upload
  const handleFileUpload = useCallback(
    async e => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        const result = await uploadDocument(file);
        if (result) {
          setFormData(prev => ({
            ...prev,
            attachments: [
              ...prev.attachments,
              { name: file.name, path: result.path, url: result.url },
            ],
          }));
        }
      } catch (error) {
        console.error('Upload failed:', error);
        showError('שגיאה בהעלאת הקובץ');
      }
    },
    [uploadDocument, showError],
  );

  // Remove attachment
  const removeAttachment = useCallback(index => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  }, []);

  // Validate current step
  const validateStep = useCallback(
    step => {
      const newErrors = {};

      if (step === 0) {
        if (!formData.master_vendor_id) {
          newErrors.master_vendor_id = 'יש לבחור ספק';
        }
      } else if (step === 1) {
        if (selectedItems.length === 0) {
          newErrors.items = 'יש לבחור לפחות פריט אחד';
        }
      } else if (step === 2) {
        const hasPrice = formData.total_price || selectedItems.some(item => item.line_price);
        if (!hasPrice) {
          newErrors.total_price = 'יש להזין מחיר כולל או מחיר לפריט';
        }
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [formData, selectedItems],
  );

  // Navigate to next step
  const goToNextStep = useCallback(() => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    }
  }, [currentStep, validateStep]);

  // Navigate to previous step
  const goToPrevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!validateStep(2)) return;

    setIsSubmitting(true);
    try {
      const quoteData = {
        project_id: effectiveProjectId,
        master_vendor_id: formData.master_vendor_id,
        total_price: formData.total_price ? parseFloat(formData.total_price) : null,
        validity_date: formData.validity_date || null,
        lead_time_days: formData.lead_time_days ? parseInt(formData.lead_time_days) : null,
        payment_terms: formData.payment_terms || null,
        notes: formData.notes || null,
        status: selectImmediately ? 'selected' : formData.status,
        attachments: formData.attachments,
      };

      let quoteId;

      if (quote?.id) {
        await updateQuote({ id: quote.id, data: quoteData });
        quoteId = quote.id;

        // Remove existing quote items that were deleted
        const existingIds = existingQuoteItems?.map(qi => qi.id) || [];
        const currentIds = selectedItems.filter(si => si.isExisting).map(si => si.id);
        const toDelete = existingIds.filter(id => !currentIds.includes(id));
        for (const id of toDelete) {
          await removeQuoteItem(id);
        }
      } else {
        const result = await insertQuote(quoteData);
        quoteId = result?.[0]?.id || result?.id;
      }

      if (!quoteId) {
        throw new Error('Failed to save quote');
      }

      // Save new line items
      for (const item of selectedItems) {
        if (item.isExisting) continue; // Skip existing items (already in DB)

        await insertQuoteItem({
          quote_id: quoteId,
          project_item_ids: [item.sourceId],
          item_type_ids: [],
          excluded_item_ids: [],
          line_price: item.line_price ? parseFloat(item.line_price) : null,
          line_notes: item.line_notes || null,
        });
      }

      // If "select immediately" is checked, run the full selection logic
      if (selectImmediately && !quote?.id) {
        await selectQuote(quoteId);
      }

      showSuccess(quote?.id ? 'הצעת המחיר עודכנה בהצלחה' : 'הצעת המחיר נוצרה בהצלחה');
      onClose();
    } catch (error) {
      console.error('Error saving quote:', error);
      showError('שגיאה בשמירת הצעת המחיר');
      setErrors(prev => ({ ...prev, submit: error.message }));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateStep,
    effectiveProjectId,
    formData,
    quote,
    selectedItems,
    existingQuoteItems,
    selectImmediately,
    selectQuote,
    insertQuote,
    updateQuote,
    insertQuoteItem,
    removeQuoteItem,
    showSuccess,
    showError,
    onClose,
  ]);

  // Register submit function for Enter key (only on last step)
  useEffect(() => {
    if (onSubmitRef && currentStep === STEPS.length - 1) {
      onSubmitRef(handleSubmit);
    }
  }, [onSubmitRef, handleSubmit, currentStep]);

  const isLoading =
    vendorsLoading ||
    itemsLoading ||
    masterItemsLoading ||
    floorsLoading ||
    spacesLoading ||
    quoteItemsLoading;

  const isEditMode = !!quote?.id;

  // Render Step 1: Quote Details
  const renderDetailsStep = () => (
    <div className={modalStyles.formGrid}>
      {/* Vendor Selection */}
      <div className={modalStyles.field}>
        <label className={modalStyles.fieldLabel}>
          <span className={modalStyles.fieldRequired}>*</span>
          ספק
        </label>
        {showNewVendorForm ? (
          <div className={styles.newVendorForm}>
            <input
              type="text"
              className={modalStyles.fieldInput}
              value={newVendorName}
              onChange={e => setNewVendorName(e.target.value)}
              placeholder="שם הספק החדש"
              autoFocus
            />
            <select
              className={modalStyles.fieldSelect}
              value={newVendorTypeId}
              onChange={e => setNewVendorTypeId(e.target.value)}
            >
              <option value="">-- בחר סוג ספק --</option>
              {vendorTypesGrouped?.map(category => (
                <optgroup key={category.category} label={category.category}>
                  {category.types.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <div className={styles.newVendorActions}>
              <button
                type="button"
                className={styles.createVendorButton}
                onClick={handleCreateVendor}
                disabled={isCreatingVendor || !newVendorName.trim() || !newVendorTypeId}
              >
                {isCreatingVendor ? 'יוצר...' : 'צור ספק'}
              </button>
              <button
                type="button"
                className={styles.cancelNewVendorButton}
                onClick={() => {
                  setShowNewVendorForm(false);
                  setNewVendorName('');
                  setNewVendorTypeId('');
                }}
              >
                ביטול
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.vendorSelectRow}>
            <select
              className={`${modalStyles.fieldSelect} ${errors.master_vendor_id ? modalStyles.error : ''}`}
              value={formData.master_vendor_id}
              onChange={e => handleFieldChange('master_vendor_id', e.target.value)}
              disabled={isLoading}
            >
              <option value="">בחר ספק</option>
              {filteredMasterVendors.map(vendor => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={styles.addVendorButton}
              onClick={() => setShowNewVendorForm(true)}
              title="הוסף ספק חדש"
            >
              <Plus size={18} />
            </button>
          </div>
        )}
        {errors.master_vendor_id && (
          <div className={modalStyles.fieldError}>{errors.master_vendor_id}</div>
        )}
      </div>

      {/* Status */}
      <div className={modalStyles.field}>
        <label className={modalStyles.fieldLabel}>סטטוס</label>
        <select
          className={modalStyles.fieldSelect}
          value={formData.status}
          onChange={e => handleFieldChange('status', e.target.value)}
        >
          {QUOTE_STATUSES.map(status => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      </div>

      {/* Validity Date and Lead Time */}
      <div className={modalStyles.formRow}>
        <div className={modalStyles.field}>
          <label className={modalStyles.fieldLabel}>תוקף ההצעה</label>
          <input
            type="date"
            className={modalStyles.fieldInput}
            value={formData.validity_date}
            onChange={e => handleFieldChange('validity_date', e.target.value)}
          />
        </div>
        <div className={modalStyles.field}>
          <label className={modalStyles.fieldLabel}>זמן אספקה (ימים)</label>
          <input
            type="number"
            className={modalStyles.fieldInput}
            value={formData.lead_time_days}
            onChange={e => handleFieldChange('lead_time_days', e.target.value)}
            placeholder="0"
          />
        </div>
      </div>

      {/* Payment Terms */}
      <div className={modalStyles.field}>
        <label className={modalStyles.fieldLabel}>תנאי תשלום</label>
        <input
          type="text"
          className={modalStyles.fieldInput}
          value={formData.payment_terms}
          onChange={e => handleFieldChange('payment_terms', e.target.value)}
          placeholder="לדוגמה: 50% מקדמה, 50% באספקה"
        />
      </div>

      {/* Notes */}
      <div className={modalStyles.field}>
        <label className={modalStyles.fieldLabel}>הערות</label>
        <textarea
          className={modalStyles.fieldTextarea}
          value={formData.notes}
          onChange={e => handleFieldChange('notes', e.target.value)}
          placeholder="הערות נוספות להצעה..."
          rows={2}
        />
      </div>

      {/* Attachments */}
      <div className={modalStyles.field}>
        <label className={modalStyles.fieldLabel}>קבצים מצורפים</label>
        <div className={styles.attachmentsSection}>
          {formData.attachments.length > 0 && (
            <div className={styles.attachmentsList}>
              {formData.attachments.map((att, idx) => (
                <div key={idx} className={styles.attachmentItem}>
                  <File size={16} />
                  <span className={styles.attachmentName}>{att.name}</span>
                  <button
                    type="button"
                    className={styles.removeAttachment}
                    onClick={() => removeAttachment(idx)}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className={styles.uploadButton}>
            <input
              type="file"
              onChange={handleFileUpload}
              disabled={isUploading}
              style={{ display: 'none' }}
            />
            <Paperclip size={16} />
            {isUploading ? 'מעלה...' : 'הוסף קובץ'}
          </label>
        </div>
      </div>
    </div>
  );

  // Render Step 2: Item Selection
  const renderItemsStep = () => {
    const isSelectedOnly = itemListMode === 'selected-only';
    return (
      <div className={styles.itemSelectionStep}>
        {isSelectedOnly ? (
          <p className={styles.modeHint}>
            מציג רק את הפריטים של הקטגוריה שנבחרה. אפשר להסיר פריטים שאינם רלוונטיים.
          </p>
        ) : (
          /* Filters (only shown in full mode) */
          <div className={styles.itemFilters}>
            <div className={styles.searchBox}>
              <MagnifyingGlass size={18} />
              <input
                type="text"
                placeholder="חיפוש פריט..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>
            <select
              className={styles.spaceFilter}
              value={spaceFilter}
              onChange={e => setSpaceFilter(e.target.value)}
            >
              {spaceOptions.map(space => (
                <option key={space.id} value={space.id}>
                  {space.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {errors.items && <div className={modalStyles.fieldError}>{errors.items}</div>}

        {/* Selected count */}
        <div className={styles.selectedCount}>נבחרו {selectedItems.length} פריטים</div>

        {/* Items list */}
        <div className={styles.itemsList}>
          {filteredItems.length === 0 ? (
            <div className={styles.noItems}>
              <Info size={24} />
              <span>לא נמצאו פריטים</span>
            </div>
          ) : (
            filteredItems.map(item => {
              const isSelected = isItemSelected(item.id, item.type);
              return (
                <div
                  key={`${item.type}-${item.id}`}
                  className={`${styles.selectableItem} ${isSelected ? styles.selected : ''}`}
                  onClick={() => toggleItemSelection(item)}
                >
                  <div className={styles.itemCheckbox}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className={styles.checkbox}
                    />
                  </div>
                  <div className={styles.itemDetails}>
                    <span className={styles.itemName}>{item.displayName}</span>
                    <span className={styles.itemMeta}>{item.spaceName}</span>
                  </div>
                  {item.estimatedCost && (
                    <span className={styles.itemEstimate}>
                      ₪{item.estimatedCost.toLocaleString()}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Switch to full mode button (only in selected-only) */}
        {isSelectedOnly && (
          <button
            type="button"
            className={styles.showAllBtn}
            onClick={() => setItemListMode('full')}
          >
            + הוסף פריטים נוספים מקטגוריות אחרות
          </button>
        )}
      </div>
    );
  };

  // Render Step 3: Review & Pricing
  const renderReviewStep = () => (
    <div className={styles.reviewStep}>
      {/* Total price override */}
      <div className={modalStyles.field}>
        <label className={modalStyles.fieldLabel}>מחיר כולל (₪)</label>
        <input
          type="number"
          className={`${modalStyles.fieldInput} ${errors.total_price ? modalStyles.error : ''}`}
          value={formData.total_price}
          onChange={e => handleFieldChange('total_price', e.target.value)}
          placeholder="השאר ריק לחישוב אוטומטי מהפריטים"
        />
        {errors.total_price && <div className={modalStyles.fieldError}>{errors.total_price}</div>}
      </div>

      {/* Select immediately checkbox - only for new quotes */}
      {!quote?.id && (
        <div className={styles.selectImmediatelyOption}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={selectImmediately}
              onChange={e => setSelectImmediately(e.target.checked)}
              className={styles.checkbox}
            />
            <span>בחר הצעה זו מיד</span>
          </label>
          <span className={styles.checkboxHint}>
            ההצעה תיבחר אוטומטית ותקושר לפריטים הרלוונטיים
          </span>
        </div>
      )}

      {/* Selected items with pricing */}
      <div className={styles.reviewItemsSection}>
        <h4 className={styles.reviewSectionTitle}>פריטים בהצעה ({selectedItems.length})</h4>

        {selectedItems.length === 0 ? (
          <div className={styles.noItems}>
            <Info size={24} />
            <span>לא נבחרו פריטים</span>
          </div>
        ) : (
          <div className={styles.reviewItemsList}>
            {selectedItems.map(item => (
              <div key={item.id} className={styles.reviewItem}>
                <div className={styles.reviewItemInfo}>
                  <span className={styles.reviewItemName}>{item.displayName}</span>
                </div>
                <div className={styles.reviewItemPricing}>
                  <div className={styles.priceInputWrapper}>
                    <input
                      type="number"
                      className={styles.reviewPriceInput}
                      value={item.line_price}
                      onChange={e => updateSelectedItem(item.id, 'line_price', e.target.value)}
                      placeholder="מחיר"
                    />
                    <span className={styles.currencyLabel}>₪</span>
                  </div>
                  <input
                    type="text"
                    className={styles.reviewNotesInput}
                    value={item.line_notes}
                    onChange={e => updateSelectedItem(item.id, 'line_notes', e.target.value)}
                    placeholder="הערות"
                  />
                </div>
                <button
                  type="button"
                  className={styles.removeItemButton}
                  onClick={() => removeSelectedItem(item.id)}
                  title="הסר פריט"
                >
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Calculated total */}
      {!formData.total_price && selectedItems.some(i => i.line_price) && (
        <div className={styles.calculatedTotal}>
          סה״כ מחושב: ₪
          {selectedItems
            .reduce((sum, i) => sum + (parseFloat(i.line_price) || 0), 0)
            .toLocaleString()}
        </div>
      )}
    </div>
  );

  return (
    <div className={modalStyles.modal} dir="rtl" onClick={e => e.stopPropagation()}>
      {/* Header */}
      <div className={modalStyles.header}>
        <h2 className={modalStyles.headerTitle}>
          {isEditMode ? 'עריכת הצעת מחיר' : 'הצעת מחיר חדשה'}
        </h2>
        {/* Step indicator */}
        <div className={styles.stepIndicator}>
          {STEPS.map((step, idx) => (
            <div
              key={step.id}
              className={`${styles.step} ${idx === currentStep ? styles.active : ''} ${idx < currentStep ? styles.completed : ''}`}
            >
              <span className={styles.stepNumber}>{idx + 1}</span>
              <span className={styles.stepLabel}>{step.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className={modalStyles.content}>
        {errors.submit && <div className={modalStyles.error}>{errors.submit}</div>}

        {isLoading ? (
          <div className={styles.loadingState}>טוען...</div>
        ) : (
          <>
            {currentStep === 0 && renderDetailsStep()}
            {currentStep === 1 && renderItemsStep()}
            {currentStep === 2 && renderReviewStep()}
          </>
        )}
      </div>

      {/* Footer with navigation */}
      <div className={modalStyles.footer}>
        <div className={styles.footerNav}>
          {currentStep > 0 && (
            <button
              type="button"
              className={styles.navButton}
              onClick={goToPrevStep}
              disabled={isSubmitting}
            >
              <CaretRight size={16} />
              הקודם
            </button>
          )}
        </div>

        <div className={styles.footerActions}>
          <button
            type="button"
            className={modalStyles.cancelButton}
            onClick={onClose}
            disabled={isSubmitting}
          >
            ביטול
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              type="button"
              className={modalStyles.primaryButton}
              onClick={goToNextStep}
              disabled={isLoading}
            >
              הבא
              <CaretLeft size={16} />
            </button>
          ) : (
            <button
              type="button"
              className={`${modalStyles.primaryButton} ${isSubmitting ? modalStyles.loading : ''}`}
              onClick={handleSubmit}
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting ? 'שומר...' : isEditMode ? 'עדכן הצעה' : 'צור הצעה'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteFormModal;
